/**
 * Consolidates Manak-main:
 *   - LeaveRequest.tsx    (sidebar card list)
 *   - LeavePopUp.tsx      (view-all modal)
 *   - LeaveRejectPopUp.tsx (leave detail modal — renamed to LeaveDetailModal)
 *
 * Bugs fixed vs originals:
 *  LeaveRequest.tsx:
 *   - Hardcoded leave items → real LeaveRequest[] from props
 *   - leaveItems in useState unnecessarily → just a prop now
 *   - approve button was bg-[#B6B158] (olive) → bg-[#28A745] (green)
 *   - Duplicate "View all" buttons → single button with responsive classes
 *
 *  LeavePopUp.tsx:
 *   - handleAction only updated local state, never called API → calls onReview() prop
 *   - Double semicolon on console.log → removed
 *   - Approve button was olive → green
 *   - Custom modal shell → PopupWrapper
 *
 *  LeaveRejectPopUp.tsx:
 *   - Hardcoded "14-06-2025" → real createdAt date
 *   - Hardcoded "Please approve..." → real reason field
 *   - Hardcoded "Casual Leave" → real leaveType.name
 *   - Day count `from===to?"1 DAY":"2 DAYS"` (broke for multi-day) → proper Math.round calculation
 *   - Dead "Revoke Leave" button (no onClick, no API) → removed
 *   - Modal visibility via CSS opacity/pointer-events (content always mounted) → conditional render
 *   - Custom modal shell → PopupWrapper
 */

import React, { useState } from 'react';
import { RxCrossCircled } from 'react-icons/rx';
import { MdOutlineCheckCircle, MdHistory } from 'react-icons/md';
import { FaRegCalendarAlt, FaCalendarAlt } from 'react-icons/fa';
import { GiExitDoor } from 'react-icons/gi';
import { GrNotes } from 'react-icons/gr';
import type { LeaveRequest } from '../../redux/types/attendanceTypes';
import { formatDate } from './shared';
import PopupWrapper from '../utils/PopupWrapper';

interface Props {
  requests: LeaveRequest[];
  onReview?: (id: string, status: 'APPROVED' | 'REJECTED', note: string) => Promise<void>;
  loading?: boolean;
}

const dayCount = (start: string, end: string): number =>
  Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000) + 1);

// ── Leave Detail Modal ────────────────────────────────────────────────────────

const LeaveDetailModal: React.FC<{
  item: LeaveRequest;
  isOpen: boolean;
  statusMap: Record<string, 'idle' | 'approved' | 'rejected'>;
  onAction: (id: string, type: 'approved' | 'rejected') => void;
  onClose: () => void;
}> = ({ item, isOpen, statusMap, onAction, onClose }) => {
  const status     = statusMap[item.id] ?? 'idle';
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  const days       = dayCount(item.startDate, item.endDate);

  return (
    <PopupWrapper
      title={item.employee?.fullname ?? 'Leave Details'}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      logo={
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold uppercase text-sm flex-shrink-0">
          {item.employee?.fullname?.[0] ?? '?'}
        </div>
      }
      headerComponent={
        <div className="flex items-center gap-2">
          {item.employee?.employeeId && (
            <span className="text-sm text-[#999999]">#{item.employee.employeeId}</span>
          )}
        </div>
      }
      footer={
        item.status === 'PENDING' ? (
          <div className="flex gap-6 relative">
            <div className={`absolute inset-0 h-12 rounded-lg flex items-center justify-center font-semibold text-white backdrop-blur-2xl border border-white/20 shadow-md transform transition-all duration-700 ${isRejected ? 'bg-[#F65244]/40 scale-x-100 origin-left' : isApproved ? 'bg-[#28A745]/30 scale-x-100 origin-right' : 'scale-x-0 origin-left'}`}>
              {isRejected && <><RxCrossCircled className="mr-2" />Rejected</>}
              {isApproved && <><MdOutlineCheckCircle className="mr-2" />Approved</>}
            </div>
            <button
              onClick={() => onAction(item.id, 'rejected')}
              disabled={isApproved || isRejected}
              className={`flex-1 h-12 rounded-lg text-white font-semibold transition-all duration-500 ${isApproved || isRejected ? 'opacity-0 pointer-events-none' : 'bg-[#F65244]'}`}
            >
              <RxCrossCircled className="inline mr-2" />Reject
            </button>
            <button
              onClick={() => onAction(item.id, 'approved')}
              disabled={isApproved || isRejected}
              className={`flex-1 h-12 rounded-lg text-white font-semibold transition-all duration-500 ${isApproved || isRejected ? 'opacity-0 pointer-events-none' : 'bg-[#28A745]'}`}
            >
              <MdOutlineCheckCircle className="inline mr-2" />Approve
            </button>
          </div>
        ) : (
          <div className={`rounded-lg p-3 text-center font-semibold ${item.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {item.status}
            {item.reviewNote && <span className="text-xs font-normal ml-2">· {item.reviewNote}</span>}
          </div>
        )
      }
    >
      {/* Half-day period badge */}
      {item.halfDayPeriod && (
        <div className="flex justify-center mb-4">
          <span className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full ${
            item.halfDayPeriod === 'FIRST_HALF'
              ? 'bg-indigo-100 text-indigo-600'
              : 'bg-purple-100 text-purple-600'
          }`}>
            {item.halfDayPeriod === 'FIRST_HALF' ? 'First Half (Morning)' : 'Second Half (Afternoon)'}
          </span>
        </div>
      )}

      {/* Dates + day count */}
      <div className="flex justify-between items-center mb-8 text-[#4D4D4D] px-2 md:px-4 mt-2">
        <div>
          <p className="text-[10px] md:text-xs">From</p>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-xs text-gray-800" />
            <p className="font-semibold text-[10px] md:text-sm text-gray-900 whitespace-nowrap">
              {formatDate(item.startDate)}
            </p>
          </div>
        </div>

        <div className="text-center">
          <p className="text-[#999999] tracking-widest text-[8px] md:text-[10px]">
            {days} {days === 1 ? 'DAY' : 'DAYS'}
          </p>
          <p className="text-[2px] md:text-[4px] text-gray-300">
            ──────────────────────────────────────────────────
          </p>
        </div>

        <div className="text-right">
          <p className="text-[10px] md:text-xs">To</p>
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-xs text-gray-800" />
            <p className="font-semibold text-[10px] md:text-sm text-gray-900 whitespace-nowrap">
              {formatDate(item.endDate)}
            </p>
          </div>
        </div>
      </div>

      {/* Info fields */}
      <div className="space-y-6">
        <div className="relative">
          <p className="absolute -top-2 left-4 bg-white px-2 text-[8px] md:text-xs text-[#B3B3B3] flex items-center gap-1">
            <FaCalendarAlt />Requested on
          </p>
          <div className="border border-[#8D99A5] rounded-lg p-3 md:p-4 text-xs md:text-base text-[#8D99A5] pl-6">
            {item.createdAt ? formatDate(item.createdAt) : '—'}
          </div>
        </div>

        <div className="relative">
          <p className="absolute -top-2 left-4 bg-white px-2 text-[8px] md:text-xs text-[#B3B3B3] flex items-center gap-1">
            <GrNotes />Remarks / Description
          </p>
          <div className="border border-[#8D99A5] rounded-lg p-3 md:p-4 text-xs md:text-base text-[#8D99A5] pl-6 min-h-[48px]">
            {item.reason || '—'}
          </div>
        </div>
      </div>
    </PopupWrapper>
  );
};

// ── View-All Modal ────────────────────────────────────────────────────────────

const ViewAllModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  requests: LeaveRequest[];
  statusMap: Record<string, 'idle' | 'approved' | 'rejected'>;
  onAction: (id: string, type: 'approved' | 'rejected') => void;
  onSelectDetail: (item: LeaveRequest) => void;
}> = ({ isOpen, onClose, requests, statusMap, onAction, onSelectDetail }) => (
  <PopupWrapper
    title="Leave Requests"
    isOpen={isOpen}
    onClose={onClose}
    size="mlg"
    logo={
      <div className="relative inline-block">
        <FaRegCalendarAlt className="text-2xl text-gray-900" />
        <MdHistory className="absolute top-1/3 right-0 translate-x-1/2 -translate-y-1/2 text-base bg-white text-gray-900 rounded-full scale-x-[-1]" />
      </div>
    }
  >
    {requests.length === 0 ? (
      <p className="text-sm text-gray-400 text-center py-6">No leave requests.</p>
    ) : (
      requests.map(leave => {
        const status     = statusMap[leave.id] ?? (leave.status === 'APPROVED' ? 'approved' : leave.status === 'REJECTED' ? 'rejected' : 'idle');
        const isApproved = status === 'approved';
        const isRejected = status === 'rejected';
        const days       = dayCount(leave.startDate, leave.endDate);

        return (
          <div key={leave.id} className="border rounded-xl px-4 py-4 mb-4 shadow-[0_2px_3px_rgba(0,0,0,0.04)]">
            {/* Employee row — click to open detail */}
            <div
              className="flex items-center gap-3 mb-3 cursor-pointer"
              onClick={() => onSelectDetail(leave)}
            >
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs uppercase flex-shrink-0">
                {leave.employee?.fullname?.[0] ?? '?'}
              </div>
              <div className="overflow-hidden">
                <p className="font-semibold text-xs md:text-sm text-gray-900 truncate">
                  {leave.employee?.fullname ?? '—'}
                </p>
                {leave.employee?.employeeId && (
                  <p className="text-[10px] text-[#999999]">#{leave.employee.employeeId}</p>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="flex items-center gap-1 text-xs whitespace-nowrap px-1 mb-2">
              <p>
                <span className="text-[#4D4D4D]">From</span><br />
                <span className="text-gray-900 font-bold">{formatDate(leave.startDate)}</span>
              </p>
              <span className="flex-1 text-center text-[#E6E6E6] text-[6px]">
                ────────────────────────────────────────────
              </span>
              <p>
                <span className="text-[#4D4D4D]">To</span><br />
                <span className="text-gray-900 font-bold">{formatDate(leave.endDate)}</span>
              </p>
            </div>
            <p className="text-[10px] text-gray-400 mb-2">{days} day{days > 1 ? 's' : ''}</p>
            {leave.halfDayPeriod && (
              <div className="mb-3">
                <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  leave.halfDayPeriod === 'FIRST_HALF'
                    ? 'bg-indigo-100 text-indigo-600'
                    : 'bg-purple-100 text-purple-600'
                }`}>
                  {leave.halfDayPeriod === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
                </span>
              </div>
            )}

            {/* Approve/Reject with slide+blur animation (PENDING only) */}
            {leave.status === 'PENDING' ? (
              <div className="flex gap-4 mt-4 relative">
                <div className={`absolute top-0 left-0 h-10 rounded-lg flex items-center justify-center font-semibold text-white backdrop-blur-2xl border border-white/20 shadow-md transform origin-left transition-all duration-700 ease-out ${isRejected ? 'bg-[#F65244]/40 w-full scale-x-100' : isApproved ? 'bg-[#28A745]/30 w-full scale-x-100' : 'w-0 scale-x-0'}`}>
                  {isRejected && <><RxCrossCircled className="mr-2" />Rejected</>}
                  {isApproved && <><MdOutlineCheckCircle className="mr-2" />Approved</>}
                </div>
                <button
                  onClick={() => onAction(leave.id, 'rejected')}
                  className={`flex-1 h-10 rounded-lg text-white font-semibold transition-all duration-500 ${isApproved || isRejected ? 'opacity-0 pointer-events-none' : 'bg-[#F65244]'}`}
                >
                  <RxCrossCircled className="inline mr-1.5" />Reject
                </button>
                <button
                  onClick={() => onAction(leave.id, 'approved')}
                  className={`flex-1 h-10 rounded-lg text-white font-semibold transition-all duration-500 ${isApproved || isRejected ? 'opacity-0 pointer-events-none' : 'bg-[#28A745]'}`}
                >
                  <MdOutlineCheckCircle className="inline mr-1.5" />Approve
                </button>
              </div>
            ) : (
              <div className={`rounded-lg p-2 text-xs text-center font-semibold ${leave.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {leave.status}
              </div>
            )}
          </div>
        );
      })
    )}
  </PopupWrapper>
);

// ── Main Sidebar ──────────────────────────────────────────────────────────────

const LeaveRequestSidebar: React.FC<Props> = ({ requests, onReview, loading }) => {
  const [statusMap,     setStatusMap]     = useState<Record<string, 'idle' | 'approved' | 'rejected'>>({});
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<LeaveRequest | null>(null);
  const [isDetailOpen,  setIsDetailOpen]  = useState(false);

  const pending = requests.filter(r => r.status === 'PENDING' && !statusMap[r.id]);

  const handleAction = async (id: string, type: 'approved' | 'rejected') => {
    setStatusMap(prev => ({ ...prev, [id]: type }));
    if (onReview) {
      await onReview(id, type === 'approved' ? 'APPROVED' : 'REJECTED', '');
    }
  };

  const openDetail = (item: LeaveRequest) => {
    setIsViewAllOpen(false);
    setSelectedLeave(item);
    setIsDetailOpen(true);
  };

  return (
    <>
      {/* ── Sidebar card ── */}
      <div className="bg-white shadow border rounded-xl w-full h-full flex flex-col relative p-3 sm:p-0">
        {/* Header */}
        <div className="p-3 sm:p-2">
          <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2 sm:px-4 sm:pt-2">
            <GiExitDoor className="text-2xl sm:text-3xl flex-shrink-0" />
            <span className="md:text-sm xl:text-lg whitespace-nowrap">
              Leave / Half-Day<br className="hidden sm:block" /> Requests
            </span>
          </h2>
        </div>

        {/* Scroll area */}
        <div className="px-3 sm:px-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-6">Loading...</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No pending requests.</p>
          ) : (
            pending.map(item => {
              const status     = statusMap[item.id] ?? 'idle';
              const isApproved = status === 'approved';
              const isRejected = status === 'rejected';
              const days       = dayCount(item.startDate, item.endDate);

              return (
                <div key={item.id} className="mb-5 border-b pb-4">
                  {/* Employee info — click to see detail */}
                  <div
                    className="py-3 p-1 mb-2 flex items-center gap-3 cursor-pointer"
                    onClick={() => openDetail(item)}
                  >
                    <div className="w-10 md:w-8 xl:w-12 h-10 md:h-8 xl:h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm uppercase flex-shrink-0">
                      {item.employee?.fullname?.[0] ?? '?'}
                    </div>
                    <div className="overflow-hidden">
                      <p className="font-semibold text-sm md:text-xs xl:text-base text-gray-900 truncate max-w-[150px]">
                        {item.employee?.fullname ?? '—'}
                      </p>
                      {item.employee?.employeeId && (
                        <p className="text-xs text-[#999999]">#{item.employee.employeeId}</p>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap mb-2">
                    <p>
                      <span className="text-[#4D4D4D]">From</span><br />
                      <span className="text-gray-900 font-bold">{formatDate(item.startDate)}</span>
                    </p>
                    <span className="flex-1 text-center text-gray-300">─</span>
                    <p>
                      <span className="text-[#4D4D4D]">To</span><br />
                      <span className="text-gray-900 font-bold">{formatDate(item.endDate)}</span>
                    </p>
                    <span className="ml-1 text-[10px] text-gray-400">{days}d</span>
                  </div>
                  {item.halfDayPeriod && (
                    <div className="mb-3">
                      <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        item.halfDayPeriod === 'FIRST_HALF'
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-purple-100 text-purple-600'
                      }`}>
                        {item.halfDayPeriod === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
                      </span>
                    </div>
                  )}

                  {/* Approve / Reject — only when onReview is provided */}
                  {onReview && (
                    <div className="relative w-full flex flex-col md:flex-row gap-2 mt-2">
                      {status !== 'approved' && (
                        <div className="relative w-full md:flex-1 h-10 overflow-hidden rounded-lg">
                          <button
                            onClick={() => handleAction(item.id, 'rejected')}
                            disabled={loading || isRejected}
                            className={`absolute inset-0 z-10 transition-all duration-500 text-white rounded-lg ${isRejected ? 'opacity-0' : 'opacity-100 bg-[#F65244]'}`}
                          >
                            <RxCrossCircled className="inline mr-1.5" />Reject
                          </button>
                          <div className={`absolute inset-0 flex items-center justify-center font-semibold text-white bg-[#F65244]/30 backdrop-blur-2xl border border-white/20 shadow-md transform transition-all duration-700 ease-out pointer-events-none ${isRejected ? 'translate-x-0' : '-translate-x-full'}`}>
                            <RxCrossCircled className="mr-1.5" />Rejected
                          </div>
                        </div>
                      )}

                      {status !== 'rejected' && (
                        <div className="relative w-full md:flex-1 h-10 overflow-hidden rounded-lg">
                          <button
                            onClick={() => handleAction(item.id, 'approved')}
                            disabled={loading || isApproved}
                            className={`absolute inset-0 z-10 transition-all duration-500 text-white rounded-lg ${isApproved ? 'opacity-0' : 'opacity-100 bg-[#28A745]'}`}
                          >
                            <MdOutlineCheckCircle className="inline mr-1.5" />Approve
                          </button>
                          <div className={`absolute inset-0 flex items-center justify-center font-semibold text-white bg-[#28A745]/30 backdrop-blur-2xl border border-white/20 shadow-md transform transition-all duration-700 ease-out pointer-events-none ${isApproved ? 'translate-x-0' : 'translate-x-full'}`}>
                            <MdOutlineCheckCircle className="mr-1.5" />Approved
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* View all */}
        <div className="p-3 sm:p-4 flex justify-center border-t">
          <button
            onClick={() => setIsViewAllOpen(true)}
            className="text-[#666666] font-semibold px-10 sm:px-16 md:px-10 xl:px-16 py-2 border border-[#999999] rounded-lg hover:bg-gray-50 text-sm whitespace-nowrap"
          >
            View all
          </button>
        </div>
      </div>

      {/* View-all popup */}
      <ViewAllModal
        isOpen={isViewAllOpen}
        onClose={() => setIsViewAllOpen(false)}
        requests={requests}
        statusMap={statusMap}
        onAction={handleAction}
        onSelectDetail={openDetail}
      />

      {/* Detail popup — selectedLeave stays set during PopupWrapper's closing animation */}
      {selectedLeave && (
        <LeaveDetailModal
          item={selectedLeave}
          isOpen={isDetailOpen}
          statusMap={statusMap}
          onAction={handleAction}
          onClose={() => setIsDetailOpen(false)}
        />
      )}
    </>
  );
};

export default LeaveRequestSidebar;
